import React, { ReactNode } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { UsageByYearMonth } from '../../store/usageDataSlice';
import translate from '../translate';

const DEFAULT_CURRENCY = 'EUR';

// a solid chain of colors is [ '#004986', '#18A4CF', '#A3A3A3', '#E3E3E3', ]
// they can be used to visualize the current and the most recent three years
const YEAR_TO_COLOR = ['#18A4CF', '#A3A3A3'];

const BAR_SIZE = 20;

interface DataPoint {
    month: number;

    [key: number]: number;
}

interface Texts {
    months: { [k: number]: string };
}

type FormatPriceFunction = (amountAndCurrency: {
    amount: number;
    currency: string;
}) => string;

interface TextsPlus extends Texts {
    _utils: {
        formatPrice: FormatPriceFunction;
    };
}

interface Props {
    height: number;
    usageHistory: UsageByYearMonth;
    strings: TextsPlus;
}

interface State {
    data: DataPoint[];
}

class UsageOverviewChart extends React.Component<Props, State> {
    state: State = {
        data: this.transformUsageByYearMonth(this.props.usageHistory),
    };

    componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any) {
        this.setState({
            data: this.transformUsageByYearMonth(nextProps.usageHistory),
        });
    }

    mapDataPointToMonth = (index: ReactNode): ReactNode => {
        if (typeof index !== 'number') {
            return index;
        }
        return this.mapMonthToName(index);
    };

    mapMonthToName = (month: number): string => {
        return this.props.strings.months[month] || String(month);
    };

    mapValueToCurrency = (amount: number): string => {
        const currency = this.getFirstCurrency();
        return this.props.strings._utils.formatPrice({ amount, currency });
    };

    getFirstCurrency = () => {
        const years = Object.keys(this.props.usageHistory);
        if (years.length === 0) {
            return DEFAULT_CURRENCY;
        }
        const year = years[0];
        const months = Object.keys(this.props.usageHistory[year]);
        if (months.length === 0) {
            return DEFAULT_CURRENCY;
        }
        const month = months[0];
        return (
            this.props.usageHistory[year][month].currency || DEFAULT_CURRENCY
        );
    };

    transformUsageByYearMonth(input: UsageByYearMonth): DataPoint[] {
        const result: DataPoint[] = [];
        for (let month = 1; month <= 12; ++month) {
            const point: DataPoint = {
                month: Number(month),
            };
            Object.keys(input).forEach((year) => {
                const value = input[year]?.[month]?.amount;
                if (value !== undefined) {
                    point[Number(year)] = value;
                }
            });
            result.push(point);
        }

        return this.addZeroValuesForPastYears(result);
    }

    addZeroValuesForPastYears(preResult: DataPoint[]): DataPoint[] {
        const nowYear = new Date().getFullYear();
        const nowMonth = new Date().getMonth() + 1;

        const usedYears: number[] = [];
        preResult.forEach((it) =>
            Object.keys(it).forEach((year) => {
                const yearAsNumber = Number(year);
                if (
                    Number.isInteger(yearAsNumber) &&
                    !usedYears.includes(yearAsNumber)
                ) {
                    usedYears.push(yearAsNumber);
                }
            })
        );
        return preResult.map((it) => {
            usedYears.forEach((year) => {
                const beforeNow = year < nowYear || it.month < nowMonth;
                if (it[year] === undefined && beforeNow) {
                    it[year] = 0;
                }
            });
            return it;
        });
    }

    renderLine = (year: string) => {
        const inputYear = Number(year);
        const nowYear = new Date().getFullYear();

        const yearPast = nowYear - inputYear;
        if (YEAR_TO_COLOR.length <= yearPast || yearPast < 0) {
            return null;
        }
        const color = YEAR_TO_COLOR[yearPast];

        if (yearPast === 0) {
            return (
                <Bar
                    dataKey={year}
                    stroke={color}
                    fill={color}
                    yAxisId={1}
                    xAxisId={1}
                />
            );
        }

        return (
            <Line
                dataKey={year}
                stroke={color}
                fill={color}
                yAxisId={1}
                xAxisId={1}
            />
        );
    };

    render() {
        const years = Object.keys(this.props.usageHistory);

        return (
            <div>
                <ResponsiveContainer width={'100%'} height={this.props.height}>
                    <ComposedChart data={this.state.data} barSize={BAR_SIZE}>
                        <Legend verticalAlign={'top'} />
                        <Tooltip
                            axisId={1}
                            labelFormatter={this.mapDataPointToMonth}
                            formatter={(value) => typeof value === 'number' ? this.mapValueToCurrency(value) : value}
                        />
                        <CartesianGrid />
                        <XAxis
                            xAxisId={1}
                            dataKey={'month'}
                            type={'number'}
                            domain={[1, 12]}
                            interval={'preserveStartEnd'}
                            padding={{
                                left: BAR_SIZE / 2,
                                right: BAR_SIZE / 2,
                            }}
                            tickFormatter={this.mapMonthToName}
                        />
                        <YAxis
                            yAxisId={1}
                            type={'number'}
                            interval={'preserveStartEnd'}
                            width={100}
                            tickFormatter={this.mapValueToCurrency}
                        />
                        {years.map(this.renderLine)}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }
}

export default translate('UsageOverviewChart')(UsageOverviewChart);
